import { useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { DutyScheduleResponse, dutySchedulesApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface Options {
  /** Called after a successful delete — the detail page uses it to leave the now-gone duty schedule. */
  onDeleted?: () => void;
}

export function useDutyScheduleDelete({ onDeleted }: Options = {}) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dutySchedulesApi.deleteDutySchedule({ id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.DutySchedules] });
      showSuccess(t("dutySchedule.delete.success"));
      onDeleted?.();
    },
  });

  const confirmAndDelete = (dutySchedule: DutyScheduleResponse) => {
    showConfirmationDialog({
      dialogTitleText: t("dutySchedule.delete.confirmTitle"),
      dialogText: t("dutySchedule.delete.confirmText"),
      agreeText: t("dutySchedule.delete.confirm"),
      agreeColor: "error",
      onClose: (confirmed) => {
        if (confirmed) {
          deleteMutation.mutate(dutySchedule.id);
        }
      },
    });
  };

  return { confirmAndDelete, isDeleting: deleteMutation.isPending };
}
